import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import produce from 'immer'

import { useToastContext } from '../../../../../contexts'
import { getHandleableError } from '../../../../../utils/errors/isXiaError'
import { requestUpdateEInvoiceConfig, useEInvoiceConfig } from '../../data'
import { EInvoiceDataForm } from './EInvoice.form'

export type FormValues = {
  maxFileSize: number
  xIncActive: boolean
  xIncVersion: string
  xIncVersionComparer: 0 | 1
  zugfActive: boolean
  zugfVersion: string
  zugfVersionComparer: 0 | 1
}

export type FormFieldName = keyof FormValues

export const EInvoiceData: React.FC = () => {
  const { t } = useTranslation()
  const { data, isLoading, mutate } = useEInvoiceConfig()
  const { showToast } = useToastContext()

  const [isRequestRunning, setIsRequestRunning] = useState(false)

  const handleSubmit = async (formData: FormValues) => {
    try {
      setIsRequestRunning(true)

      await submitData(formData)
      mutate()

      // show success toast
      showToast({
        severity: 'success',
        summary: t('buyerSettings.eInvoice.save.messages.success.summary'),
        detail: t('buyerSettings.eInvoice.save.messages.success.detail'),
        life: 3000,
      })
    } catch (error) {
      let handleableError = getHandleableError(error)
      let errorMessage = handleableError.message

      // show error toast
      showToast({
        style: {},
        closable: true,
        severity: 'error',
        sticky: true,
        summary: t('buyerSettings.eInvoice.save.messages.failure.summary'),
        detail: errorMessage,
      })
    } finally {
      setIsRequestRunning(false)
    }
  }

  const submitData = async (formData: FormValues) => {
    const emailSettings = produce(data, draft => {
      if (draft) {
        draft.maxFileSize = formData.maxFileSize
        draft.xInc = {
          active: formData.xIncActive,
          version: formData.xIncVersion,
          versionComparer: formData.xIncVersionComparer,
        }
        draft.zugf = {
          active: formData.zugfActive,
          version: formData.zugfVersion,
          versionComparer: formData.zugfVersionComparer,
        }
      }
    })

    if (emailSettings) await requestUpdateEInvoiceConfig(emailSettings)
  }

  return (
    <EInvoiceDataForm
      data={data}
      handleSubmit={handleSubmit}
      isRequestRunning={isRequestRunning}
    />
  )
}
